import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from "@fluentui/react-components";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { dataTimeService, overtimeAssigments } from "../../services";
import appErrorToast from "../common/AppErrorToast";
import { ICreateRealizationRequest } from "../../services/types/overtimeAssigments";
import { useParams } from "react-router-dom";
import { AppButton, AppDateSelect, AppTimeInput, TaskLinksField } from "../common";
import { GENERATED_COMPENSATION_REALIZATION_DATE_TOOLTIP } from "../../utils/linkedCompensation";

interface IRealizeAssignmentDialogProps {
  open: boolean;
  date?: string;
  assignmentDuration?: string;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setReload: Dispatch<SetStateAction<boolean>>;
  reload: boolean;
  isCommision: boolean;
  isOvertime: boolean;
  isGenerated?: boolean;
}
type Inputs = {
  realizationDate: string;
  duration: string;
};

const useStyles = makeStyles({
  formContaier: {
    display: "flex",
    flexDirection: "column",
  },
  input: {
    marginBottom: "1rem",
    width: "100%",
  },
});

const RealizeAssignmentDialog: React.FC<IRealizeAssignmentDialogProps> = ({
  open,
  setOpen,
  date,
  assignmentDuration,
  setReload,
  reload,
  isCommision,
  isOvertime,
  isGenerated = false,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm();
  const styles = useStyles();
  const [links, setLinks] = useState<Array<string>>([]);
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setLinks([]);
    reset({ realizationDate: date, duration: assignmentDuration});
  };

  useEffect(() => {
    if (open && date) {
      reset({ realizationDate: date, duration: assignmentDuration });
    }
  }, [open, date, assignmentDuration, reset]);

  async function handleFormSubmit (data: Inputs) {
    setIsLoading(true);

    if (links.length < 1 && (isCommision || isOvertime)) {
      appErrorToast("Dodaj linki do zadań");
      setIsLoading(false);
      return;
    }
    const requestBody: ICreateRealizationRequest = {
      realizationDate: data.realizationDate,
      duration: dataTimeService.convertTimeToMin(data.duration),
      rejectionDescription: "",
      urls: links,
      id: id,
    };

    await overtimeAssigments.realizeAssignment(requestBody);

    setOpen(false);
    resetForm();
    setIsLoading(false);
    setReload(!reload);
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Rozlicz</DialogTitle>
          <DialogContent>
            <form
              className={styles.formContaier}
            >
              <AppDateSelect
                label="Data realizacji"
                className={styles.input}
                formHandler={register("realizationDate", {
                  required: true,
                })}
                defaultValue={date}
                infoTooltip={
                  isGenerated
                    ? GENERATED_COMPENSATION_REALIZATION_DATE_TOOLTIP
                    : undefined
                }
              />
              <AppTimeInput
                className={styles.input}
                label="Czas trwania"
                type="time"
                formHandler={register("duration", {
                  required: {value: true, message: "Czas trwania jest wymagany"},
                  min: 1,
                })}
                control={control}
                errors={errors}
                defaultValue={assignmentDuration}
              />
              {(isOvertime || isCommision) && (
                <TaskLinksField links={links} onChange={setLinks} />
              )}
            </form>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={resetForm}>
                Anuluj
              </Button>
            </DialogTrigger>
            <AppButton
              text="Rozlicz"
              disabled={isLoading}
              appearance="primary"
              onClick={handleSubmit((data) => {
                handleFormSubmit(data as Inputs);
              })}
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default RealizeAssignmentDialog;
